import React, { useState } from 'react';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MobileComparisonAccordionTable from './MobileComparisonAccordionTable';
import Image from '../../Image/Image';

const KEYPOINT_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/KeyPointVector.svg';
const CARD_SECTION_GAP = '24px';

const SeoDetailModuleMobileComponent = ({ data }) => {
  const { title, description, footer, cards = [], faq = [] } = data || {};
  const hasFAQ = Array.isArray(faq) && faq.length > 0;
  const html = (value) => ({ __html: typeof value === 'string' ? value : '' });

  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (index) => setOpenIndex(index === openIndex ? null : index);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        backgroundColor: '#E3E6E8',
        borderRadius: '48px',
        padding: '32px 16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: '2rem',
          lineHeight: '2.125rem',
          letterSpacing: '-0.03em',
          color: '#162F3B',
          textAlign: 'center',
          whiteSpace: 'pre-line',
          width: '100%',
          px: { xs: '24px', sm: '20px', md: '16px' },
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          boxSizing: 'border-box',
        }}
        component="div"
        dangerouslySetInnerHTML={html(title)}
      />

      {/* Description */}
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: '1.25rem',
          lineHeight: '1.5rem',
          letterSpacing: '-0.03em',
          color: '#162F3B',
          textAlign: 'center',
          mt: '16px',
          mb: '40px',
          whiteSpace: 'pre-line',
          alignSelf: 'stretch',
          boxSizing: 'border-box',
        }}
        component="div"
        dangerouslySetInnerHTML={html(description)}
      />

      {/* Cards */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
        }}
      >
        {cards.map((card, index) => (
          <ScrollFadeWrapper key={card.number} delay={index * 0.1}>
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#FFFFFF',
                borderRadius: '34px',
                padding: '32px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: CARD_SECTION_GAP,
                boxShadow: '0px 6px 16px #162F3B1F, 0px 1px 2px #A2ACB11F',
                boxSizing: 'border-box',
              }}
            >
              {/* Number icon above title/text */}
              <Box
                sx={{
                  position: 'relative',
                  width: '80px',
                  height: '80px',
                }}
              >
                <Image
                  width={80}
                  height={80}
                  src={KEYPOINT_VECTOR_URL}
                  alt="Key point illustration"
                  sx={{ width: '100%', height: '100%' }}
                />
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontFamily: 'Pally, Inter',
                    fontWeight: 700,
                    fontSize: '2.125rem',
                    lineHeight: '2.125rem',
                    color: '#162F3B',
                  }}
                >
                  {card.number}
                </Typography>
              </Box>

              {/* Title */}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  lineHeight: '1.5rem',
                  color: '#162F3B',
                  letterSpacing: '-0.03em',
                }}
                component="div"
                dangerouslySetInnerHTML={html(card.title)}
              />

              {/* Description */}
              <Box
                sx={{
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  color: '#162F3B',
                  letterSpacing: '-0.002em',
                  whiteSpace: 'pre-line',
                  '& p': { m: 0, mb: '1em' },
                  '& p:last-of-type': { mb: 0 },
                  '& ul': { m: 0, mt: '1em', pl: '20px' },
                  '& li': { mb: '0.5em' },
                }}
                component="div"
                dangerouslySetInnerHTML={html(card.description)}
              />

              {/* Optional image */}
              {card.image && (
                <>
                  <Box
                    sx={{
                      backgroundColor: '#F3F6F6',
                      height: '267px',
                      borderRadius: '16px',
                      width: '100%',
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      color: '#162F3B',
                      letterSpacing: '-0.001em',
                    }}
                    component="div"
                    dangerouslySetInnerHTML={html(card.caption)}
                  />
                </>
              )}

              {/* Optional table */}
              {card.table && (
                <MobileComparisonAccordionTable
                  table={card.table}
                  defaultOpenIndex={0}
                />
              )}

              {/* Optional strong intro text */}
              {card.strongText && (
                <Typography
                  sx={{ fontWeight: 700, fontSize: '1rem', color: '#162F3B' }}
                  component="div"
                  dangerouslySetInnerHTML={html(card.strongText)}
                />
              )}

              {/* Paragraph */}
              {card.paragraph && (
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '1rem',
                    lineHeight: '1.5rem',
                    color: '#162F3B',
                    letterSpacing: '-0.002em',
                    whiteSpace: 'pre-line',
                  }}
                  component="div"
                  dangerouslySetInnerHTML={html(card.paragraph)}
                />
              )}
            </Box>
          </ScrollFadeWrapper>
        ))}
      </Box>

      {/* FAQ */}
      {hasFAQ &&
        faq.map((item, i) => {
          const isOpen = openIndex === i;
          return (
            <Box
              key={i}
              role="button"
              tabIndex={0}
              onClick={() => toggle(i)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle(i);
                }
              }}
              sx={{
                width: '100%',
                backgroundColor: '#FFFFFF',
                borderRadius: '24px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                mb: '16px',
                boxShadow: '0px 6px 16px #162F3B1F, 0px 1px 2px #A2ACB11F',
                cursor: 'pointer',
                boxSizing: 'border-box',
                transition: 'box-shadow 0.2s ease',
                outline: 'none',
                '&:focus-visible': { boxShadow: '0 0 0 3px #A2ACB166' },
              }}
            >
              {/* FAQ Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    lineHeight: '1.5rem',
                    letterSpacing: '-0.03em',
                    color: '#162F3B',
                  }}
                >
                  {item.title}
                </Typography>

                <IconButton
                  disableRipple
                  tabIndex={-1}
                  sx={{
                    width: '32px',
                    height: '32px',
                    color: '#162F3B',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 200ms.ease',
                  }}
                  aria-label={isOpen ? 'Collapse answer' : 'Expand answer'}
                >
                  <ArrowDropDownIcon sx={{ fontSize: '32px' }} />
                </IconButton>
              </Box>

              {/* FAQ Content */}
              <Collapse in={isOpen} timeout={250} unmountOnExit>
                <Typography
                  sx={{
                    mt: '12px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '1rem',
                    lineHeight: '1.5rem',
                    letterSpacing: '-0.02em',
                    color: '#162F3B',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {item.description}
                </Typography>
              </Collapse>
            </Box>
          );
        })}

      {/* Footer */}
      <Typography
        sx={{
          textAlign: 'center',
          fontFamily: 'Inter, sans-serif',
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: '1.5rem',
          letterSpacing: '-0.002em',
          color: '#162F3B',
          mt: '40px',
          alignSelf: 'stretch',
          boxSizing: 'border-box',
        }}
      >
        {footer}
      </Typography>
    </Box>
  );
};

export default SeoDetailModuleMobileComponent;
